import { z } from 'zod'
import { tenantRoleReachSchema } from '../enums/tenant-role-reach.enum.js'

/**
 * What a company role may do (docs/custom-roles.spec.md, "Permissions"): each
 * area at None, View or Edit (Edit includes View), where the area has those
 * levels, and the switches under some areas (`AREA_SWITCHES`). The areas themselves — their names, what each
 * covers, which feature it needs — are `PERMISSION_CATALOG` in
 * `lib/src/constants/permissions.ts`.
 *
 * The levels are defined once here. The role editor and every route that asks
 * for View or Edit import them, rather than listing the words again.
 */
export const PERMISSION_LEVELS = ['none', 'view', 'edit'] as const
export const permissionLevelSchema = z.enum(PERMISSION_LEVELS)
export type PermissionLevel = z.infer<typeof permissionLevelSchema>

/** A grant above None: what a route asks for when it checks View or Edit. */
export type PermissionAccess = Exclude<PermissionLevel, 'none'>

const VIEW_ONLY_LEVELS = ['none', 'view'] as const satisfies readonly PermissionLevel[]
export const viewOnlyLevelSchema = z.enum(VIEW_ONLY_LEVELS)

const EDIT_ONLY_LEVELS = ['none', 'edit'] as const satisfies readonly PermissionLevel[]
export const editOnlyLevelSchema = z.enum(EDIT_ONLY_LEVELS)

const grantsShape = {
  events: permissionLevelSchema,
  clients: permissionLevelSchema,
  spaces: permissionLevelSchema,
  furniture: permissionLevelSchema,
  venueSetup: permissionLevelSchema,
  vendors: permissionLevelSchema,
  marketing: permissionLevelSchema,
  leadStats: permissionLevelSchema,
  sales: permissionLevelSchema,
  ads: viewOnlyLevelSchema,
  people: permissionLevelSchema,
  venues: editOnlyLevelSchema,
  integrations: editOnlyLevelSchema,
  imports: editOnlyLevelSchema,
  /** Deleting an event, and clearing a venue's events. Only with Edit on Events. */
  deleteEvents: z.boolean(),
  /** Changing prices, tax and service charge. Only with Edit on Sales. */
  salesPriceList: z.boolean(),
  /** Refunding payments. Only with Edit on Sales. */
  salesRefunds: z.boolean(),
}

/**
 * Grants as the API sends them: every key. Like every response schema, not
 * strict, so a key added later is dropped by an older client rather than
 * refusing the response (`response-shapes.test.ts`). Input is strict:
 * `grantsInputSchema` in `lib/src/input/tenant-role.input.ts`.
 */
export const grantsSchema = z.object(grantsShape)
export type Grants = z.infer<typeof grantsSchema>

/**
 * Grants as stored, read so that drift only ever takes access away: a key the
 * catalog no longer has is dropped, and one it gained since, or a value it no
 * longer allows, reads as none. Never used for input, which is refused instead.
 */
export const storedGrantsSchema = z.object({
  events: permissionLevelSchema.catch('none'),
  clients: permissionLevelSchema.catch('none'),
  spaces: permissionLevelSchema.catch('none'),
  furniture: permissionLevelSchema.catch('none'),
  venueSetup: permissionLevelSchema.catch('none'),
  vendors: permissionLevelSchema.catch('none'),
  marketing: permissionLevelSchema.catch('none'),
  leadStats: permissionLevelSchema.catch('none'),
  sales: permissionLevelSchema.catch('none'),
  ads: viewOnlyLevelSchema.catch('none'),
  people: permissionLevelSchema.catch('none'),
  venues: editOnlyLevelSchema.catch('none'),
  integrations: editOnlyLevelSchema.catch('none'),
  imports: editOnlyLevelSchema.catch('none'),
  deleteEvents: z.boolean().catch(false),
  salesPriceList: z.boolean().catch(false),
  salesRefunds: z.boolean().catch(false),
})

/** The company's own built-in roles. Organization admin is not one: it can do everything, and has no row. */
const TENANT_ROLE_BUILT_INS = ['venue_manager'] as const
export const tenantRoleBuiltInSchema = z.enum(TENANT_ROLE_BUILT_INS)
export type TenantRoleBuiltIn = z.infer<typeof tenantRoleBuiltInSchema>

/** The built-in Venue manager's `builtIn`: what a staff member with no role of their own holds. */
export const VENUE_MANAGER_BUILT_IN: TenantRoleBuiltIn = 'venue_manager'

/** One of the company's roles, with how many people hold it. */
export const tenantRoleDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Which built-in role this is; null for one the company made. A built-in can't be deleted, only reset. */
  builtIn: tenantRoleBuiltInSchema.nullable(),
  reach: tenantRoleReachSchema,
  grants: grantsSchema,
  /** Active and invited people on it. */
  people: z.number().int().nonnegative(),
})
export type TenantRoleDto = z.infer<typeof tenantRoleDtoSchema>

/** The Roles page: the fixed Organization admin's head count, then the company's roles, built-in first. */
export const tenantRoleListDtoSchema = z.object({
  organizationAdmins: z.number().int().nonnegative(),
  roles: z.array(tenantRoleDtoSchema),
})
export type TenantRoleListDto = z.infer<typeof tenantRoleListDtoSchema>
